import { apiClient, PaginatedResponse, QueryParams } from './apiClient';
import {
  IssueReport,
  CreateIssueReportRequest,
  CreateIssueReportResponse,
  UpdateIssueReportRequest,
  UpdateIssueReportResponse,
  IssueReportFilters
} from '@/types/model';

const ENDPOINT = '/media-errors';

export class IssueReportService {
  /**
   * Get paginated list of issue reports with optional filters
   */
  async getAll(params?: QueryParams & IssueReportFilters): Promise<PaginatedResponse<IssueReport>> {
    return apiClient.getPaginated<IssueReport>(ENDPOINT, params);
  }

  /**
   * Get issue report by ID
   */
  async getById(id: string): Promise<IssueReport> {
    return apiClient.getById<IssueReport>(ENDPOINT, id);
  }

  /**
   * Create a new issue report
   */
  async create(data: CreateIssueReportRequest): Promise<CreateIssueReportResponse> {
    return apiClient.create<CreateIssueReportRequest, CreateIssueReportResponse>(ENDPOINT, data);
  }

  /**
   * Update an existing issue report
   */
  async update(id: string, data: UpdateIssueReportRequest): Promise<UpdateIssueReportResponse> {
    return apiClient.partialUpdate<UpdateIssueReportRequest, UpdateIssueReportResponse>(ENDPOINT, id, data);
  }

  /**
   * Delete an issue report
   */
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById(ENDPOINT, id);
  }

  /**
   * Mark issue report as resolved
   */
  async markAsResolved(id: string, notes?: string): Promise<UpdateIssueReportResponse> {
    return this.update(id, {
      status: 'RESOLVED',
      notes
    });
  }

  /**
   * Mark issue report as in progress
   */
  async markAsInProgress(id: string, assignedTo?: string): Promise<UpdateIssueReportResponse> {
    return this.update(id, {
      status: 'IN_PROGRESS',
      assignedTo
    });
  }

  /**
   * Mark issue report as rejected
   */
  async markAsRejected(id: string, notes?: string): Promise<UpdateIssueReportResponse> {
    return this.update(id, {
      status: 'IGNORED',
      notes
    });
  }

  /**
   * Assign issue report to a user
   */
  async assignTo(id: string, assignedTo: string): Promise<UpdateIssueReportResponse> {
    return this.update(id, { assignedTo });
  }

  /**
   * Update issue priority
   */
  async updatePriority(id: string, priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'): Promise<UpdateIssueReportResponse> {
    return this.update(id, { priority });
  }

  /**
   * Get issue reports statistics
   */
  async getStats(): Promise<{
    total: number;
    reportados: number;
    enProgreso: number;
    resueltos: number;
    byContentType: Record<string, number>;
    byReportType: Record<string, number>;
    byPriority: Record<string, number>;
  }> {
    return apiClient.get(`${ENDPOINT}/stats`);
  }

  /**
   * Get filtered counts for tabs
   */
  async getCounts(): Promise<{
    reportados: number;
    reparados: number;
    total: number;
  }> {
    const stats = await this.getStats();
    return {
      reportados: stats.reportados + stats.enProgreso,
      reparados: stats.resueltos,
      total: stats.total
    };
  }
}

export const issueReportService = new IssueReportService();
